import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, Select, MenuItem,
  FormControl, InputLabel, Stack, Alert, CircularProgress, Checkbox,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Chip, Tabs, Tab, Autocomplete, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import {
  Autorenew as RenewalIcon,
  SimCard as SimIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { apiService } from '../../services/api';

interface DeviceRow {
  _id: string;
  imei: string;
  name: string;
  clientName: string;
  expirationTime?: string;
  simCardId?: string;
  simCardStatus?: string;
  status: string;
}

const BulkOperationsPage: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [devices, setDevices] = useState<DeviceRow[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<any>(null);
  const [clients, setClients] = useState<any[]>([]);
  const [filterClient, setFilterClient] = useState<any>(null);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [paymentRef, setPaymentRef] = useState('');
  const [amount, setAmount] = useState(2500);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    apiService.getClients(1, 500).then((res: any) => {
      setClients(res?.data?.clients || res?.clients || []);
    }).catch(() => {});
  }, []);

  const loadDevices = async () => {
    setLoading(true);
    setError(null);
    setSelectedIds(new Set());
    try {
      const params: any = { limit: 500 };
      if (filterClient) params.clientId = filterClient._id;

      const response = await (apiService as any).api.get('/devices', { params });
      const list = response.data?.data?.devices || response.data?.devices || response.data?.data || [];

      let filtered: DeviceRow[] = list.map((d: any) => ({
        _id: d._id,
        imei: d.imei,
        name: d.name || d.imei,
        clientName: d.clientId?.name || d.clientName || '-',
        expirationTime: d.expirationTime,
        simCardId: d.simCardId?._id || d.simCardId,
        simCardStatus: d.simCardStatus || d.simCardId?.status,
        status: d.status,
      }));

      if (tab === 0) {
        // Bulk renewals: show expired or expiring devices
        filtered = filtered.filter(d => {
          if (!d.expirationTime) return true;
          const exp = new Date(d.expirationTime);
          const thirtyDaysFromNow = new Date();
          thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
          return exp < thirtyDaysFromNow;
        });
      } else {
        // Bulk SIM: show devices with SIM
        filtered = filtered.filter(d => d.simCardId);
      }

      setDevices(filtered);
    } catch (err: any) {
      setError(err.message || 'Error cargando dispositivos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDevices();
  }, [tab]);

  const toggleSelect = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedIds(next);
  };

  const toggleAll = () => {
    if (selectedIds.size === devices.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(devices.map(d => d._id)));
    }
  };

  const handleExecute = async () => {
    setConfirmOpen(false);
    setExecuting(true);
    setError(null);
    setResult(null);
    try {
      const ids = Array.from(selectedIds);
      let res: any;

      if (tab === 0) {
        res = await apiService.bulkRenew({
          deviceIds: ids,
          paymentMethod,
          paymentReference: paymentRef || undefined,
          amount,
        });
      } else {
        const simIds = devices.filter(d => selectedIds.has(d._id) && d.simCardId).map(d => d.simCardId!);
        res = await apiService.bulkSimAction({
          simCardIds: simIds,
          action: tab === 1 ? 'activate' : 'deactivate',
        });
      }

      setResult(res?.data || res);
      loadDevices();
    } catch (err: any) {
      setError(err.message || 'Error ejecutando operacion');
    } finally {
      setExecuting(false);
    }
  };

  const isExpired = (d: DeviceRow) => {
    if (!d.expirationTime) return true;
    return new Date(d.expirationTime) < new Date();
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>Operaciones en Lote</Typography>
        <Typography variant="body1" color="text.secondary">
          Renovar dispositivos o activar/desactivar SIMs en masa
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {result && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setResult(null)}>
          {result.message || `Completado: ${result.success?.length || 0} exitosos, ${result.failed?.length || 0} fallidos`}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab icon={<RenewalIcon />} label="Renovar en Lote" iconPosition="start" />
          <Tab icon={<SimIcon />} label="SIM Activar" iconPosition="start" />
          <Tab icon={<SimIcon />} label="SIM Desactivar" iconPosition="start" />
        </Tabs>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Autocomplete
              options={clients}
              getOptionLabel={(c: any) => c.name}
              value={filterClient}
              onChange={(_, val) => setFilterClient(val)}
              renderInput={(params) => <TextField {...params} label="Filtrar por cliente" size="small" />}
              sx={{ minWidth: 250 }}
              isOptionEqualToValue={(o: any, v: any) => o._id === v._id}
            />
            <Button variant="outlined" onClick={loadDevices} disabled={loading}>
              {loading ? <CircularProgress size={20} /> : 'Recargar'}
            </Button>

            {tab === 0 && (
              <>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>Metodo pago</InputLabel>
                  <Select value={paymentMethod} label="Metodo pago" onChange={(e) => setPaymentMethod(e.target.value)}>
                    <MenuItem value="cash">Efectivo</MenuItem>
                    <MenuItem value="bank_transfer">Transferencia</MenuItem>
                    <MenuItem value="other">Otro</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label="Referencia"
                  value={paymentRef}
                  onChange={(e) => setPaymentRef(e.target.value)}
                  size="small"
                  sx={{ width: 160 }}
                />
                <TextField
                  label="Monto c/u"
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(Number(e.target.value))}
                  size="small"
                  sx={{ width: 120 }}
                />
              </>
            )}
          </Stack>

          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 500 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedIds.size === devices.length && devices.length > 0}
                      indeterminate={selectedIds.size > 0 && selectedIds.size < devices.length}
                      onChange={toggleAll}
                    />
                  </TableCell>
                  <TableCell>IMEI</TableCell>
                  <TableCell>Nombre</TableCell>
                  <TableCell>Cliente</TableCell>
                  {tab === 0 ? (
                    <TableCell>Expiracion</TableCell>
                  ) : (
                    <TableCell>SIM Status</TableCell>
                  )}
                  <TableCell>Estado</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {devices.map(d => (
                  <TableRow key={d._id} hover selected={selectedIds.has(d._id)}>
                    <TableCell padding="checkbox">
                      <Checkbox checked={selectedIds.has(d._id)} onChange={() => toggleSelect(d._id)} />
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{d.imei}</TableCell>
                    <TableCell>{d.name}</TableCell>
                    <TableCell>{d.clientName}</TableCell>
                    {tab === 0 ? (
                      <TableCell>
                        {d.expirationTime ? (
                          <Chip
                            label={new Date(d.expirationTime).toLocaleDateString('es-DO')}
                            size="small"
                            color={isExpired(d) ? 'error' : 'warning'}
                          />
                        ) : (
                          <Chip label="Sin fecha" size="small" color="default" />
                        )}
                      </TableCell>
                    ) : (
                      <TableCell>
                        <Chip
                          label={d.simCardStatus || 'unknown'}
                          size="small"
                          color={d.simCardStatus === 'active' ? 'success' : 'default'}
                        />
                      </TableCell>
                    )}
                    <TableCell>
                      <Chip label={d.status} size="small" variant="outlined" />
                    </TableCell>
                  </TableRow>
                ))}
                {devices.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      No se encontraron dispositivos para esta operacion
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {selectedIds.size} de {devices.length} seleccionados
              {tab === 0 && selectedIds.size > 0 && (
                <> — Total: <strong>${(selectedIds.size * amount).toLocaleString()}</strong></>
              )}
            </Typography>
            <Button
              variant="contained"
              color={tab === 2 ? 'error' : 'primary'}
              onClick={() => setConfirmOpen(true)}
              disabled={selectedIds.size === 0 || executing}
              startIcon={executing ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {executing ? 'Ejecutando...' :
                tab === 0 ? `Renovar ${selectedIds.size} dispositivos` :
                tab === 1 ? `Activar ${selectedIds.size} SIMs` :
                `Desactivar ${selectedIds.size} SIMs`}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* Result details */}
      {result?.success?.length > 0 && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 1, color: 'success.main' }}>
              <SuccessIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
              Exitosos ({result.success.length})
            </Typography>
            {result.success.slice(0, 20).map((s: any, i: number) => (
              <Chip key={i} label={s.imei || s.iccid || s.deviceId} size="small" sx={{ mr: 0.5, mb: 0.5 }} color="success" variant="outlined" />
            ))}
            {result.success.length > 20 && <Typography variant="body2">...y {result.success.length - 20} mas</Typography>}
          </CardContent>
        </Card>
      )}
      {result?.failed?.length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 1, color: 'error.main' }}>
              <ErrorIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
              Fallidos ({result.failed.length})
            </Typography>
            {result.failed.map((f: any, i: number) => (
              <Alert key={i} severity="error" sx={{ mb: 0.5 }} variant="outlined">
                {f.imei || f.iccid || f.deviceId}: {f.error}
              </Alert>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirmar operacion</DialogTitle>
        <DialogContent>
          <Typography>
            {tab === 0 && `Renovar ${selectedIds.size} dispositivos por $${amount.toLocaleString()} c/u (Total: $${(selectedIds.size * amount).toLocaleString()})?`}
            {tab === 1 && `Activar ${selectedIds.size} SIMs en Simbase?`}
            {tab === 2 && `Desactivar ${selectedIds.size} SIMs en Simbase? Los dispositivos dejaran de reportar.`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancelar</Button>
          <Button onClick={handleExecute} variant="contained" color={tab === 2 ? 'error' : 'primary'}>
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BulkOperationsPage;
